import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { orderedTimelineSections, type Timeline, type TimelineSection } from '@peles/shared';
import {
  useRefreshTimelines,
  useReorderTimelineEntries,
  useReorderTimelineSections,
} from '../../hooks/useEventOps';

export type ActiveDragItem =
  | { kind: 'section'; section: TimelineSection }
  | { kind: 'entry'; entry: TimelineSection['entries'][number] }
  | null;

/**
 * The timeline's sections as the editor shows them, and dragging them and
 * their entries about. The order on screen is local while a drag is under
 * way (an entry crosses into another section as it is hovered over it), and
 * a drop is saved as the complete new order of what it touched — one request
 * for a section, one for an entry (naming both sections when it crossed) —
 * never a request per renumbered row. Each names the order the page saw
 * before the drop, so a stale page is refused rather than overwriting a newer
 * order. Saves go one at a time, in the order of the drops: a quick second
 * drop names the order the first one is saving, and is sent only once that is
 * stored, so it never reaches the server first. A refused or failed save puts
 * the order from before it back on screen at once, and the drops queued
 * behind it — made on top of an order that was not stored — are not sent.
 * A refetch is not taken while saves are queued, so it never shows an order
 * between two drops; once the queue empties the timeline is fetched again, so
 * one that arrived meanwhile (another planner's order, after a refusal) is
 * never left unshown.
 */
export function useTimelineDnd(timeline: Timeline | undefined) {
  const [sections, setSections] = useState<TimelineSection[]>([]);
  const dragging = useRef(false);
  const before = useRef<TimelineSection[]>([]);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const queued = useRef(0);
  const broken = useRef(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const reorderSections = useReorderTimelineSections();
  const reorderEntries = useReorderTimelineEntries();
  const refreshTimelines = useRefreshTimelines();

  useEffect(() => {
    if (timeline?.sections && !dragging.current && queued.current === 0) {
      setSections(orderedTimelineSections(timeline.sections));
    }
  }, [timeline]);

  const sectionIds = useMemo(() => new Set(sections.map((s) => s.id)), [sections]);
  const entrySection = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sections) for (const e of s.entries) map.set(e.id, s.id);
    return map;
  }, [sections]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeItem: ActiveDragItem = useMemo(() => {
    if (!activeId) return null;
    const section = sections.find((s) => s.id === activeId);
    if (section) return { kind: 'section', section };
    const owner = sections.find((s) => s.id === entrySection.get(activeId));
    const entry = owner?.entries.find((e) => e.id === activeId);
    return entry ? { kind: 'entry', entry } : null;
  }, [activeId, sections, entrySection]);

  function onDragStart({ active }: DragStartEvent) {
    dragging.current = true;
    before.current = sections;
    setActiveId(String(active.id));
  }

  function onDragOver({ active, over }: DragOverEvent) {
    if (!over) return;
    const id = String(active.id);
    const overId = String(over.id);
    if (sectionIds.has(id)) return;
    const from = entrySection.get(id);
    const to = sectionIds.has(overId) ? overId : entrySection.get(overId);
    if (!from || !to || from === to) return;

    setSections((prev) => {
      const entry = prev.find((s) => s.id === from)?.entries.find((e) => e.id === id);
      if (!entry) return prev;
      return prev.map((s) => {
        if (s.id === from) return { ...s, entries: s.entries.filter((e) => e.id !== id) };
        if (s.id !== to) return s;
        const entries = [...s.entries];
        const at = sectionIds.has(overId) ? -1 : entries.findIndex((e) => e.id === overId);
        entries.splice(at >= 0 ? at : entries.length, 0, entry);
        return { ...s, entries };
      });
    });
  }

  function finish() {
    dragging.current = false;
    setActiveId(null);
  }

  function onDragCancel() {
    finish();
    setSections(before.current);
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    finish();
    const id = String(active.id);
    const overId = over ? String(over.id) : null;

    if (sectionIds.has(id)) {
      if (!overId || !sectionIds.has(overId) || overId === id) return;
      const fromIdx = sections.findIndex((s) => s.id === id);
      const toIdx = sections.findIndex((s) => s.id === overId);
      const next = arrayMove(sections, fromIdx, toIdx);
      const previous = sections;
      setSections(next);
      if (timeline) {
        const timelineId = timeline.id;
        enqueue(
          () =>
            reorderSections.mutateAsync({
              timelineId,
              sectionIds: next.map((s) => s.id),
              expected: previous.map((s) => s.id),
            }),
          previous,
        );
      }
      return;
    }

    // An entry let go outside every section goes back where it came from.
    if (!overId) {
      setSections(before.current);
      return;
    }
    const to = entrySection.get(id);
    const from = before.current.find((s) => s.entries.some((e) => e.id === id))?.id;
    if (!to || !from) return;
    const target = sections.find((s) => s.id === to)!;
    let entries = target.entries;
    if (overId !== id && !sectionIds.has(overId)) {
      const fromIdx = entries.findIndex((e) => e.id === id);
      const toIdx = entries.findIndex((e) => e.id === overId);
      if (fromIdx >= 0 && toIdx >= 0) entries = arrayMove(entries, fromIdx, toIdx);
    }
    const next = sections.map((s) => (s.id === to ? { ...s, entries } : s));
    const unchanged =
      from === to &&
      entries.map((e) => e.id).join() ===
        before.current
          .find((s) => s.id === from)!
          .entries.map((e) => e.id)
          .join();
    setSections(next);
    if (unchanged) return;

    const previous = before.current;
    const was = (sectionId: string) =>
      previous.find((s) => s.id === sectionId)?.entries.map((e) => e.id) ?? [];
    const orders = [{ sectionId: to, entryIds: entries.map((e) => e.id), expected: was(to) }];
    if (from !== to) {
      const source = next.find((s) => s.id === from);
      if (source) {
        orders.push({
          sectionId: from,
          entryIds: source.entries.map((e) => e.id),
          expected: was(from),
        });
      }
    }
    saveEntries(orders, previous);
  }

  function saveEntries(
    orders: { sectionId: string; entryIds: string[]; expected: string[] }[],
    previous: TimelineSection[],
  ) {
    if (!timeline) return;
    const timelineId = timeline.id;
    enqueue(() => reorderEntries.mutateAsync({ timelineId, sections: orders }), previous);
  }

  /**
   * Sends a save after every save before it. Once one fails, the order from
   * before it goes back on screen and the rest queued behind it are skipped —
   * the queue starts afresh when it empties.
   */
  function enqueue(send: () => Promise<unknown>, previous: TimelineSection[]) {
    queued.current += 1;
    queue.current = queue.current.then(async () => {
      try {
        if (!broken.current) await send();
      } catch {
        broken.current = true;
        setSections(previous);
      } finally {
        queued.current -= 1;
        if (queued.current === 0) {
          broken.current = false;
          // Lands after the queue has emptied, so it is applied.
          void refreshTimelines();
        }
      }
    });
  }

  /** Puts one section's entries in the order given and saves it — "Sort by time". */
  function setSectionOrder(sectionId: string, entryIds: string[]) {
    const previous = sections;
    const section = previous.find((s) => s.id === sectionId);
    if (!section) return;
    const byId = new Map(section.entries.map((e) => [e.id, e]));
    setSections(
      previous.map((s) =>
        s.id === sectionId
          ? { ...s, entries: entryIds.map((id) => byId.get(id)!).filter(Boolean) }
          : s,
      ),
    );
    saveEntries([{ sectionId, entryIds, expected: section.entries.map((e) => e.id) }], previous);
  }

  return {
    sections,
    sensors,
    activeItem,
    saving: reorderSections.isPending || reorderEntries.isPending,
    failed: reorderSections.isError || reorderEntries.isError,
    setSectionOrder,
    handlers: { onDragStart, onDragOver, onDragEnd, onDragCancel },
  };
}
